import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Spinner from 'react-bootstrap/Spinner';
import { ArrowClockwise, PencilSquare, PlusLg, Trash3, XLg } from 'react-bootstrap-icons';

import {
    createSuperAdminLLMModel,
    deleteSuperAdminLLMModel,
    listSuperAdminLLMProviderCapabilityProfiles,
    listSuperAdminLLMModels,
    updateSuperAdminLLMModel,
    type SuperAdminLLMModel,
    type SuperAdminLLMProviderCapabilityProfile,
    type SuperAdminLLMModelUpsertPayload,
} from '../api/superadmin';

import './SuperAdminPlans-new.css';
import './SuperAdminLLMModels-new.css';

const INPUT_MODALITY_OPTIONS = ['text', 'image', 'document'];
const OUTPUT_MODALITY_OPTIONS = ['text', 'embedding'];
const PROVIDER_OPTIONS = ['openai', 'gemini', 'anthropic'] as const;
const TEMPERATURE_OPTIONS = ['yes', 'no', 'conditional'] as const;
const REASONING_OPTIONS_JSON_SAMPLE = '[\n  "low",\n  "medium",\n  "high"\n]';

type TierFormState = {
    label: string;
    max_context_tokens: string;
    input_price_per_million: string;
    output_price_per_million: string;
    cached_input_price_per_million: string;
    image_price_per_million: string;
    cached_pricing_confirmed: boolean;
};

type FormState = {
    canonical_model_id: string;
    display_name: string;
    provider: 'openai' | 'gemini' | 'anthropic';
    is_selectable: boolean;
    is_active: boolean;
    supports_multimodal: boolean;
    input_modalities: string[];
    output_modalities: string[];
    context_window: string;
    max_output_tokens: string;
    supports_reasoning: boolean;
    reasoning_effort_options_json: string;
    reasoning_effort_default: string;
    supports_temperature_mode: 'yes' | 'no' | 'conditional';
    knowledge_cutoff: string;
    notes: string;
    pricing_tiers: TierFormState[];
};

const EMPTY_TIER: TierFormState = {
    label: 'standard',
    max_context_tokens: '',
    input_price_per_million: '0',
    output_price_per_million: '0',
    cached_input_price_per_million: '0',
    image_price_per_million: '0',
    cached_pricing_confirmed: true,
};

const EMPTY_FORM: FormState = {
    canonical_model_id: '',
    display_name: '',
    provider: 'openai',
    is_selectable: true,
    is_active: true,
    supports_multimodal: true,
    input_modalities: ['text', 'image'],
    output_modalities: ['text'],
    context_window: '128000',
    max_output_tokens: '4096',
    supports_reasoning: false,
    reasoning_effort_options_json: '[]',
    reasoning_effort_default: '',
    supports_temperature_mode: 'yes',
    knowledge_cutoff: '',
    notes: '',
    pricing_tiers: [{ ...EMPTY_TIER }],
};

function mergeOrderedOptions(base: string[], selected: string[]): string[]
{
    return Array.from(new Set([...base, ...selected]));
}

function parseReasoningOptionsJson(value: string, supportsReasoning: boolean): string[]
{
    let parsed: unknown;
    try {
        parsed = JSON.parse(value);
    }
    catch {
        throw new Error('Reasoning effort options must be valid JSON.');
    }

    if (!Array.isArray(parsed)) {
        throw new Error('Reasoning effort options JSON must be an array of strings.');
    }

    const options = parsed.map((item) => {
        if (typeof item !== 'string' || !item.trim()) {
            throw new Error('Each reasoning effort option must be a non-empty string.');
        }
        return item.trim().toLowerCase();
    });
    if (new Set(options).size !== options.length) {
        throw new Error('Reasoning effort options must not contain duplicates.');
    }
    if (supportsReasoning && options.length === 0) {
        throw new Error('Add at least one reasoning effort option when reasoning is enabled.');
    }
    return options;
}

function getReasoningOptionsJsonError(value: string, supportsReasoning: boolean): string | null
{
    try {
        parseReasoningOptionsJson(value, supportsReasoning);
        return null;
    }
    catch (exc: any) {
        return exc?.message || 'Invalid reasoning effort options JSON.';
    }
}

function slugifyModelName(value: string): string
{
    return value
        .trim()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9.\s/-]+/g, ' ')
        .replace(/[/_\s]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function getAutoCanonicalModelId(form: FormState): string
{
    const slug = slugifyModelName(form.display_name);
    if (!slug) {
        return '';
    }
    return `${form.provider}/${slug}`;
}

function applyProviderDefaults(
    current: FormState,
    provider: FormState['provider'],
    profile?: SuperAdminLLMProviderCapabilityProfile,
): FormState
{
    if (!profile) {
        return {
            ...current,
            provider,
        };
    }

    const inputModalities = profile.default_input_modalities.length > 0
        ? profile.default_input_modalities
        : profile.input_modalities;
    const outputModalities = profile.default_output_modalities.length > 0
        ? profile.default_output_modalities
        : profile.output_modalities;
    const supportsReasoning = Boolean(profile.supports_reasoning_default);
    const reasoningOptions = supportsReasoning ? [...profile.reasoning_effort_options] : [];
    const preferredReasoningDefault = supportsReasoning
        ? (
            profile.reasoning_effort_default
            && reasoningOptions.includes(profile.reasoning_effort_default)
                ? profile.reasoning_effort_default
                : reasoningOptions[0] || ''
        )
        : '';

    return {
        ...current,
        provider,
        supports_multimodal: inputModalities.some((item) => item !== 'text'),
        input_modalities: [...inputModalities],
        output_modalities: [...outputModalities],
        supports_reasoning: supportsReasoning,
        reasoning_effort_options_json: JSON.stringify(reasoningOptions, null, 2),
        reasoning_effort_default: preferredReasoningDefault,
        supports_temperature_mode: profile.supports_temperature_mode_default,
    };
}

function modelToFormState(model: SuperAdminLLMModel): FormState
{
    return {
        canonical_model_id: model.canonical_model_id,
        display_name: model.display_name,
        provider: model.provider as FormState['provider'],
        is_selectable: model.is_selectable,
        is_active: model.is_active,
        supports_multimodal: model.supports_multimodal,
        input_modalities: model.input_modalities,
        output_modalities: model.output_modalities,
        context_window: String(model.context_window),
        max_output_tokens: String(model.max_output_tokens),
        supports_reasoning: model.supports_reasoning,
        reasoning_effort_options_json: JSON.stringify(model.reasoning_effort_options, null, 2),
        reasoning_effort_default: model.reasoning_effort_default || '',
        supports_temperature_mode: model.supports_temperature_mode,
        knowledge_cutoff: model.knowledge_cutoff || '',
        notes: model.notes || '',
        pricing_tiers: model.pricing_tiers.map((tier) => ({
            label: tier.label,
            max_context_tokens: tier.max_context_tokens == null ? '' : String(tier.max_context_tokens),
            input_price_per_million: tier.input_price_per_million,
            output_price_per_million: tier.output_price_per_million,
            cached_input_price_per_million: tier.cached_input_price_per_million,
            image_price_per_million: tier.image_price_per_million,
            cached_pricing_confirmed: tier.cached_pricing_confirmed,
        })),
    };
}

function buildPayload(form: FormState): SuperAdminLLMModelUpsertPayload
{
    const canonicalModelId = form.canonical_model_id.trim().toLowerCase() || getAutoCanonicalModelId(form);
    const reasoningOptions = form.supports_reasoning
        ? parseReasoningOptionsJson(form.reasoning_effort_options_json, true)
        : [];
    const reasoningDefault = form.supports_reasoning
        ? (form.reasoning_effort_default.trim().toLowerCase() || null)
        : null;
    if (reasoningDefault && !reasoningOptions.includes(reasoningDefault.trim().toLowerCase())) {
        throw new Error('Default reasoning effort must be included in the JSON options array.');
    }
    return {
        canonical_model_id: canonicalModelId,
        display_name: form.display_name.trim(),
        provider: form.provider,
        is_selectable: form.is_selectable,
        is_active: form.is_active,
        supports_multimodal: form.supports_multimodal,
        input_modalities: form.input_modalities,
        output_modalities: form.output_modalities,
        context_window: Number(form.context_window || 0),
        max_output_tokens: Number(form.max_output_tokens || 0),
        supports_reasoning: form.supports_reasoning,
        reasoning_effort_options: reasoningOptions,
        reasoning_effort_default: reasoningDefault,
        supports_temperature_mode: form.supports_temperature_mode,
        knowledge_cutoff: form.knowledge_cutoff.trim() || null,
        notes: form.notes.trim() || null,
        pricing_tiers: form.pricing_tiers.map((tier) => ({
            label: tier.label.trim(),
            max_context_tokens: tier.max_context_tokens.trim() ? Number(tier.max_context_tokens) : null,
            input_price_per_million: Number(tier.input_price_per_million || 0),
            output_price_per_million: Number(tier.output_price_per_million || 0),
            cached_input_price_per_million: Number(tier.cached_input_price_per_million || 0),
            image_price_per_million: Number(tier.image_price_per_million || 0),
            cached_pricing_confirmed: tier.cached_pricing_confirmed,
        })),
    };
}

function formatPricing(model: SuperAdminLLMModel): string
{
    const firstTier = model.pricing_tiers[0];
    if (!firstTier) {
        return 'No pricing';
    }
    return `$${Number(firstTier.input_price_per_million).toFixed(2)} in / $${Number(firstTier.output_price_per_million).toFixed(2)} out`;
}

function SuperAdminLLMModels(): React.ReactElement
{
    const [models, setModels] = useState<SuperAdminLLMModel[]>([]);
    const [providerProfiles, setProviderProfiles] = useState<Record<string, SuperAdminLLMProviderCapabilityProfile>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [searchInput, setSearchInput] = useState('');
    const [providerInput, setProviderInput] = useState('');
    const [statusInput, setStatusInput] = useState('all');
    const [appliedFilters, setAppliedFilters] = useState<{ search?: string; provider?: string; status?: string }>({});

    const [modalOpen, setModalOpen] = useState(false);
    const [editingModel, setEditingModel] = useState<SuperAdminLLMModel | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [formError, setFormError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const refresh = useCallback(async (filters = appliedFilters) => {
        setLoading(true);
        setError(null);
        try {
            const response = await listSuperAdminLLMModels(filters);
            setModels(response.items);
        }
        catch (exc: any) {
            setError(exc?.response?.data?.detail || exc?.message || 'Failed to load LLM models');
        }
        finally {
            setLoading(false);
        }
    }, [appliedFilters]);

    useEffect(() => {
        void refresh(appliedFilters);
    }, [appliedFilters, refresh]);

    useEffect(() => {
        let active = true;
        void (async () => {
            try {
                const response = await listSuperAdminLLMProviderCapabilityProfiles();
                if (!active) {
                    return;
                }
                const nextProfiles = response.items.reduce<Record<string, SuperAdminLLMProviderCapabilityProfile>>((accumulator, item) => {
                    accumulator[item.provider] = item;
                    return accumulator;
                }, {});
                setProviderProfiles(nextProfiles);
            }
            catch {
                if (active) {
                    setProviderProfiles({});
                }
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    function resetForm(): void
    {
        setEditingModel(null);
        setForm(EMPTY_FORM);
        setFormError(null);
    }

    function openCreate(): void
    {
        setEditingModel(null);
        setForm(applyProviderDefaults(EMPTY_FORM, EMPTY_FORM.provider, providerProfiles[EMPTY_FORM.provider]));
        setFormError(null);
        setModalOpen(true);
    }

    function openEdit(model: SuperAdminLLMModel): void
    {
        setEditingModel(model);
        setForm(modelToFormState(model));
        setFormError(null);
        setModalOpen(true);
    }

    function closeModal(): void
    {
        setModalOpen(false);
        setSubmitting(false);
        resetForm();
    }

    function updateForm<K extends keyof FormState>(key: K, value: FormState[K]): void
    {
        setForm((current) => ({ ...current, [key]: value }));
    }

    function handleProviderChange(provider: FormState['provider']): void
    {
        setForm((current) => applyProviderDefaults(current, provider, providerProfiles[provider]));
    }

    function toggleSelection(key: 'input_modalities' | 'output_modalities', option: string): void
    {
        const values = new Set(form[key]);
        if (values.has(option)) {
            values.delete(option);
        } else {
            values.add(option);
        }
        updateForm(key, Array.from(values) as FormState[typeof key]);
    }

    function updateTier(index: number, key: keyof TierFormState, value: string | boolean): void
    {
        setForm((current) => ({
            ...current,
            pricing_tiers: current.pricing_tiers.map((tier, tierIndex) => (
                tierIndex === index ? { ...tier, [key]: value } : tier
            )),
        }));
    }

    function addTier(): void
    {
        setForm((current) => ({
            ...current,
            pricing_tiers: [
                ...current.pricing_tiers,
                { ...EMPTY_TIER, label: `tier-${current.pricing_tiers.length + 1}` },
            ],
        }));
    }

    function removeTier(index: number): void
    {
        setForm((current) => ({
            ...current,
            pricing_tiers: current.pricing_tiers.filter((_, tierIndex) => tierIndex !== index),
        }));
    }

    async function handleSave(event: React.FormEvent): Promise<void>
    {
        event.preventDefault();
        setFormError(null);
        setSuccess(null);
        setSubmitting(true);
        try {
            const payload = buildPayload(form);
            if (editingModel) {
                await updateSuperAdminLLMModel(editingModel.id, payload);
                setSuccess(`Updated ${payload.display_name}.`);
            } else {
                await createSuperAdminLLMModel(payload);
                setSuccess(`Created ${payload.display_name}.`);
            }
            closeModal();
            await refresh(appliedFilters);
        }
        catch (exc: any) {
            setFormError(exc?.response?.data?.detail || exc?.message || 'Save failed');
        }
        finally {
            setSubmitting(false);
        }
    }

    async function handleDelete(model: SuperAdminLLMModel): Promise<void>
    {
        const confirmed = window.confirm(`Delete model "${model.display_name}"? This will archive it and remove it from active selections.`);
        if (!confirmed) {
            return;
        }
        setDeletingId(model.id);
        setSuccess(null);
        setError(null);
        try {
            await deleteSuperAdminLLMModel(model.id);
            setSuccess(`Archived ${model.display_name}.`);
            await refresh(appliedFilters);
        }
        catch (exc: any) {
            setError(exc?.response?.data?.detail || exc?.message || 'Delete failed');
        }
        finally {
            setDeletingId(null);
        }
    }

    const totalActive = useMemo(() => models.filter((model) => model.is_active).length, [models]);
    const totalSelectable = useMemo(() => models.filter((model) => model.is_active && model.is_selectable).length, [models]);
    const activeProviderProfile = providerProfiles[form.provider];
    const autoCanonicalModelId = editingModel ? form.canonical_model_id : getAutoCanonicalModelId(form);
    const availableInputModalities = mergeOrderedOptions(
        INPUT_MODALITY_OPTIONS,
        form.input_modalities,
    );
    const availableOutputModalities = mergeOrderedOptions(
        OUTPUT_MODALITY_OPTIONS,
        form.output_modalities,
    );
    let reasoningOptionsForForm: string[] = [];
    try {
        reasoningOptionsForForm = parseReasoningOptionsJson(form.reasoning_effort_options_json, false);
    }
    catch {
        reasoningOptionsForForm = [];
    }
    const reasoningOptionsJsonError = form.supports_reasoning
        ? getReasoningOptionsJsonError(form.reasoning_effort_options_json, true)
        : null;
    const availableTemperatureOptions = mergeOrderedOptions(
        [...TEMPERATURE_OPTIONS],
        [form.supports_temperature_mode],
    ) as FormState['supports_temperature_mode'][];

    return (
        <div className="sa-plans-page">
            <div className="sa-plans-breadcrumbs">Super Admin · LLM Models</div>
            <div className="sa-plans-header-row">
                <div>
                    <h1 className="sa-plans-title">LLM Models</h1>
                    <p className="sa-plans-subtitle">
                        Manage the platform-wide LLM model catalogue, capabilities, and pricing tiers shown across admin configuration flows.
                    </p>
                </div>
                <div className="sa-plans-actions">
                    <button className="sa-plans-btn sa-plans-btn-icon" onClick={() => void refresh(appliedFilters)} disabled={loading}>
                        <ArrowClockwise size={14} /> Refresh
                    </button>
                    <button className="sa-plans-btn sa-plans-btn-primary sa-plans-btn-icon" onClick={openCreate}>
                        <PlusLg size={14} /> Add Model
                    </button>
                </div>
            </div>

            <div className="sa-models-summary-grid">
                <div className="sa-orgbilling-quota-tile">
                    <div className="sa-orgbilling-quota-label">Models</div>
                    <div className="sa-orgbilling-quota-value">{models.length}</div>
                    <div className="sa-orgbilling-quota-sub">Total catalog entries in the current filter set.</div>
                </div>
                <div className="sa-orgbilling-quota-tile">
                    <div className="sa-orgbilling-quota-label">Active</div>
                    <div className="sa-orgbilling-quota-value">{totalActive}</div>
                    <div className="sa-orgbilling-quota-sub">Models available to the platform runtime.</div>
                </div>
                <div className="sa-orgbilling-quota-tile">
                    <div className="sa-orgbilling-quota-label">Selectable</div>
                    <div className="sa-orgbilling-quota-value">{totalSelectable}</div>
                    <div className="sa-orgbilling-quota-sub">Models visible in admin-facing selectors.</div>
                </div>
            </div>

            {success && <div className="sa-plans-success">{success}</div>}
            {error && <div className="sa-plans-error">{error}</div>}

            <div className="sa-plans-card">
                <div className="sa-orgbilling-filters">
                    <input
                        className="sa-orgbilling-filter-input"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder="Search model name or canonical id"
                    />
                    <select className="sa-orgbilling-filter-input" value={providerInput} onChange={(event) => setProviderInput(event.target.value)}>
                        <option value="">All providers</option>
                        {PROVIDER_OPTIONS.map((provider) => (
                            <option key={provider} value={provider}>{provider}</option>
                        ))}
                    </select>
                    <select className="sa-orgbilling-filter-input" value={statusInput} onChange={(event) => setStatusInput(event.target.value)}>
                        <option value="all">All statuses</option>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="selectable">Selectable</option>
                    </select>
                    <button
                        className="sa-orgbilling-filter-btn"
                        type="button"
                        onClick={() => setAppliedFilters({
                            search: searchInput.trim() || undefined,
                            provider: providerInput || undefined,
                            status: statusInput === 'all' ? undefined : statusInput,
                        })}
                    >
                        Search
                    </button>
                    <button
                        className="sa-orgbilling-filter-clear"
                        type="button"
                        onClick={() => {
                            setSearchInput('');
                            setProviderInput('');
                            setStatusInput('all');
                            setAppliedFilters({});
                        }}
                    >
                        Clear
                    </button>
                </div>

                {loading ? (
                    <div className="sa-plans-page-loading">
                        <Spinner animation="border" size="sm" /> Loading LLM models…
                    </div>
                ) : models.length === 0 ? (
                    <div className="sa-plans-empty">No LLM models matched the current filters.</div>
                ) : (
                    <table className="sa-plans-table" data-responsive-cards="true">
                        <thead>
                            <tr>
                                <th>Model</th>
                                <th>Provider</th>
                                <th>Capabilities</th>
                                <th>Pricing</th>
                                <th>Status</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {models.map((model) => (
                                <tr key={model.id}>
                                    <td data-label="Model">
                                        <strong>{model.display_name}</strong>
                                        <div className="sa-plans-cell-sub">{model.canonical_model_id}</div>
                                    </td>
                                    <td data-label="Provider">{model.provider}</td>
                                    <td data-label="Capabilities">
                                        <div>{model.input_modalities.join(', ')}</div>
                                        <div className="sa-plans-cell-sub">
                                            {model.context_window.toLocaleString()} ctx · {model.max_output_tokens.toLocaleString()} out
                                        </div>
                                    </td>
                                    <td data-label="Pricing">
                                        <div>{formatPricing(model)}</div>
                                        <div className="sa-plans-cell-sub">{model.pricing_tiers.length} tier{model.pricing_tiers.length === 1 ? '' : 's'}</div>
                                    </td>
                                    <td data-label="Status">
                                        <span className={`sa-plans-pill ${model.is_active ? 'sa-plans-pill-active' : 'sa-plans-pill-inactive'}`}>
                                            {model.is_active ? 'active' : 'inactive'}
                                        </span>
                                        <span className={`sa-plans-pill ${model.is_selectable ? 'sa-plans-pill-free' : 'sa-plans-pill-custom'} sa-models-pill-spaced`}>
                                            {model.is_selectable ? 'selectable' : 'hidden'}
                                        </span>
                                    </td>
                                    <td className="sa-plans-cell-actions" data-label="Actions" data-card-actions="true">
                                        <button className="sa-plans-icon-btn" type="button" onClick={() => openEdit(model)}>
                                            <PencilSquare size={14} /> Edit
                                        </button>
                                        <button
                                            className="sa-plans-icon-btn sa-plans-icon-btn-danger"
                                            type="button"
                                            onClick={() => void handleDelete(model)}
                                            disabled={deletingId === model.id}
                                        >
                                            {deletingId === model.id
                                                ? <><Spinner animation="border" size="sm" /> Deleting…</>
                                                : <><Trash3 size={14} /> Delete</>}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="sa-plans-modal-overlay" role="dialog" aria-modal="true">
                    <form className="sa-models-modal" onSubmit={(event) => void handleSave(event)}>
                        <div className="sa-models-modal-header">
                            <div>
                                <h2 className="sa-plans-modal-title">{editingModel ? 'Edit LLM Model' : 'New LLM Model'}</h2>
                                <p className="sa-models-modal-subtitle">Keep canonical ids stable and update the shared catalog from one place.</p>
                            </div>
                            <button type="button" className="sa-models-close-btn" onClick={closeModal} aria-label="Close">
                                <XLg size={16} />
                            </button>
                        </div>
                        {formError && <div className="sa-plans-error">{formError}</div>}

                        <div className="sa-models-section">
                            <div className="sa-models-section-title">Basic Info</div>
                            <div className="sa-plans-form-grid">
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-model-id">Canonical Model ID</label>
                                    <input
                                        id="llm-model-id"
                                        className="sa-plans-input"
                                        value={autoCanonicalModelId}
                                        disabled
                                        placeholder="openai/gpt-5.4"
                                    />
                                    {!editingModel && (
                                        <div className="sa-plans-cell-sub">Auto-generated from provider and display name.</div>
                                    )}
                                </div>
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-provider">Provider</label>
                                    <select
                                        id="llm-provider"
                                        className="sa-plans-input"
                                        value={form.provider}
                                        onChange={(event) => handleProviderChange(event.target.value as FormState['provider'])}
                                        disabled={Boolean(editingModel)}
                                    >
                                        {PROVIDER_OPTIONS.map((provider) => (
                                            <option key={provider} value={provider}>{provider}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sa-plans-form-full">
                                    <label className="sa-plans-label" htmlFor="llm-display-name">Display Name</label>
                                    <input
                                        id="llm-display-name"
                                        className="sa-plans-input"
                                        value={form.display_name}
                                        onChange={(event) => updateForm('display_name', event.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-context-window">Context Window</label>
                                    <input
                                        id="llm-context-window"
                                        className="sa-plans-input"
                                        type="number"
                                        min="1"
                                        value={form.context_window}
                                        onChange={(event) => updateForm('context_window', event.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-max-output">Max Output Tokens</label>
                                    <input
                                        id="llm-max-output"
                                        className="sa-plans-input"
                                        type="number"
                                        min="0"
                                        value={form.max_output_tokens}
                                        onChange={(event) => updateForm('max_output_tokens', event.target.value)}
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-temp-mode">Temperature Support</label>
                                    <select
                                        id="llm-temp-mode"
                                        className="sa-plans-input"
                                        value={form.supports_temperature_mode}
                                        onChange={(event) => updateForm('supports_temperature_mode', event.target.value as FormState['supports_temperature_mode'])}
                                    >
                                        {availableTemperatureOptions.map((option) => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="sa-plans-label" htmlFor="llm-knowledge-cutoff">Knowledge Cutoff</label>
                                    <input
                                        id="llm-knowledge-cutoff"
                                        className="sa-plans-input"
                                        value={form.knowledge_cutoff}
                                        onChange={(event) => updateForm('knowledge_cutoff', event.target.value)}
                                        placeholder="2026-05"
                                    />
                                </div>
                                <div className="sa-plans-form-full">
                                    <label className="sa-plans-label" htmlFor="llm-notes">Notes</label>
                                    <textarea
                                        id="llm-notes"
                                        className="sa-plans-textarea"
                                        value={form.notes}
                                        onChange={(event) => updateForm('notes', event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="sa-models-section">
                            <div className="sa-models-section-title">Capabilities</div>
                            {(activeProviderProfile?.notes || activeProviderProfile) && (
                                <div className="sa-plans-cell-sub sa-models-provider-note">
                                    {activeProviderProfile
                                        ? `Provider defaults are preselected for ${activeProviderProfile.provider}, but you can override them for this model.`
                                        : ''}
                                    {activeProviderProfile?.notes ? ` ${activeProviderProfile.notes}` : ''}
                                </div>
                            )}
                            <div className="sa-models-chip-group">
                                <div className="sa-models-chip-title">Input Modalities</div>
                                <div className="sa-models-chip-wrap">
                                    {availableInputModalities.map((option) => (
                                        <label key={option} className={`sa-models-chip ${form.input_modalities.includes(option) ? 'active' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={form.input_modalities.includes(option)}
                                                onChange={() => toggleSelection('input_modalities', option)}
                                            />
                                            <span>{option}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div className="sa-models-chip-group">
                                <div className="sa-models-chip-title">Output Modalities</div>
                                <div className="sa-models-chip-wrap">
                                    {availableOutputModalities.map((option) => (
                                        <label key={option} className={`sa-models-chip ${form.output_modalities.includes(option) ? 'active' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={form.output_modalities.includes(option)}
                                                onChange={() => toggleSelection('output_modalities', option)}
                                            />
                                            <span>{option}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="sa-models-toggle-grid">
                                <label className={`sa-plans-toggle-row ${form.is_active ? 'sa-plans-toggle-row-active' : ''}`}>
                                    <span className="sa-plans-toggle-row-text">
                                        <span className="sa-plans-toggle-row-title">Active</span>
                                        <span className="sa-plans-toggle-row-sub">Keep this model available to the runtime.</span>
                                    </span>
                                    <input type="checkbox" checked={form.is_active} onChange={(event) => updateForm('is_active', event.target.checked)} />
                                </label>
                                <label className={`sa-plans-toggle-row ${form.is_selectable ? 'sa-plans-toggle-row-active' : ''}`}>
                                    <span className="sa-plans-toggle-row-text">
                                        <span className="sa-plans-toggle-row-title">Selectable</span>
                                        <span className="sa-plans-toggle-row-sub">Show this model in admin-facing selection controls.</span>
                                    </span>
                                    <input type="checkbox" checked={form.is_selectable} onChange={(event) => updateForm('is_selectable', event.target.checked)} />
                                </label>
                                <label className={`sa-plans-toggle-row ${form.supports_multimodal ? 'sa-plans-toggle-row-active' : ''}`}>
                                    <span className="sa-plans-toggle-row-text">
                                        <span className="sa-plans-toggle-row-title">Supports Multimodal</span>
                                        <span className="sa-plans-toggle-row-sub">Use when the model accepts more than text input.</span>
                                    </span>
                                    <input type="checkbox" checked={form.supports_multimodal} onChange={(event) => updateForm('supports_multimodal', event.target.checked)} />
                                </label>
                                <label className={`sa-plans-toggle-row ${form.supports_reasoning ? 'sa-plans-toggle-row-active' : ''}`}>
                                    <span className="sa-plans-toggle-row-text">
                                        <span className="sa-plans-toggle-row-title">Supports Reasoning</span>
                                        <span className="sa-plans-toggle-row-sub">Enable reasoning-effort controls for this model.</span>
                                    </span>
                                    <input type="checkbox" checked={form.supports_reasoning} onChange={(event) => updateForm('supports_reasoning', event.target.checked)} />
                                </label>
                            </div>

                            {form.supports_reasoning && (
                                <div className="sa-models-reasoning-block">
                                    <label className="sa-plans-label" htmlFor="llm-reasoning-options-json">
                                        Reasoning Effort Options
                                    </label>
                                    <textarea
                                        id="llm-reasoning-options-json"
                                        className={`sa-plans-input sa-models-json-input ${reasoningOptionsJsonError ? 'sa-models-json-input-invalid' : ''}`}
                                        value={form.reasoning_effort_options_json}
                                        onChange={(event) => updateForm('reasoning_effort_options_json', event.target.value)}
                                        placeholder={REASONING_OPTIONS_JSON_SAMPLE}
                                        rows={6}
                                        spellCheck={false}
                                        aria-invalid={Boolean(reasoningOptionsJsonError)}
                                    />
                                    <div className="sa-models-json-help">
                                        Enter a JSON array containing the exact effort values supported by this model. Values are normalized to lowercase.
                                    </div>
                                    {reasoningOptionsJsonError && (
                                        <div className="sa-models-json-error" role="alert">{reasoningOptionsJsonError}</div>
                                    )}
                                    <div className="sa-models-default-row">
                                        <label className="sa-plans-label" htmlFor="llm-reasoning-default">Default Reasoning Effort</label>
                                        <select
                                            id="llm-reasoning-default"
                                            className="sa-plans-input"
                                            value={form.reasoning_effort_default}
                                            onChange={(event) => updateForm('reasoning_effort_default', event.target.value)}
                                        >
                                            <option value="">Select a default</option>
                                            {reasoningOptionsForForm.map((option) => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="sa-models-section">
                            <div className="sa-models-section-header">
                                <div className="sa-models-section-title">Pricing Tiers</div>
                                <button type="button" className="sa-plans-btn sa-plans-btn-icon" onClick={addTier}>
                                    <PlusLg size={14} /> Add Tier
                                </button>
                            </div>
                            <div className="sa-models-tier-list">
                                {form.pricing_tiers.map((tier, index) => (
                                    <div key={`${tier.label}-${index}`} className="sa-models-tier-card">
                                        <div className="sa-models-tier-card-header">
                                            <strong>Tier {index + 1}</strong>
                                            {form.pricing_tiers.length > 1 && (
                                                <button type="button" className="sa-plans-icon-btn sa-plans-icon-btn-danger" onClick={() => removeTier(index)}>
                                                    <Trash3 size={14} /> Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="sa-plans-form-grid">
                                            <div>
                                                <label className="sa-plans-label">Label</label>
                                                <input className="sa-plans-input" value={tier.label} onChange={(event) => updateTier(index, 'label', event.target.value)} required />
                                            </div>
                                            <div>
                                                <label className="sa-plans-label">Max Context Tokens</label>
                                                <input className="sa-plans-input" value={tier.max_context_tokens} onChange={(event) => updateTier(index, 'max_context_tokens', event.target.value)} placeholder="Leave blank for open-ended" />
                                            </div>
                                            <div>
                                                <label className="sa-plans-label">Input Price / 1M</label>
                                                <input className="sa-plans-input" type="number" min="0" step="0.0001" value={tier.input_price_per_million} onChange={(event) => updateTier(index, 'input_price_per_million', event.target.value)} />
                                            </div>
                                            <div>
                                                <label className="sa-plans-label">Output Price / 1M</label>
                                                <input className="sa-plans-input" type="number" min="0" step="0.0001" value={tier.output_price_per_million} onChange={(event) => updateTier(index, 'output_price_per_million', event.target.value)} />
                                            </div>
                                            <div>
                                                <label className="sa-plans-label">Cached Input Price / 1M</label>
                                                <input className="sa-plans-input" type="number" min="0" step="0.0001" value={tier.cached_input_price_per_million} onChange={(event) => updateTier(index, 'cached_input_price_per_million', event.target.value)} />
                                            </div>
                                            <div>
                                                <label className="sa-plans-label">Image Price / 1M</label>
                                                <input className="sa-plans-input" type="number" min="0" step="0.0001" value={tier.image_price_per_million} onChange={(event) => updateTier(index, 'image_price_per_million', event.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="sa-plans-modal-actions">
                            <button type="button" className="sa-plans-btn" onClick={closeModal} disabled={submitting}>Cancel</button>
                            <button type="submit" className="sa-plans-btn sa-plans-btn-primary sa-plans-btn-icon" disabled={submitting}>
                                {submitting
                                    ? <><Spinner animation="border" size="sm" /> Saving…</>
                                    : editingModel
                                        ? <><PencilSquare size={14} /> Save Changes</>
                                        : <><PlusLg size={14} /> Create Model</>}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

export default SuperAdminLLMModels;
